import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FundListFilterChips from './FundListFilterChips';
import FundListFilterModal from './FundListFilterModal';
import { FundListFilterItem } from './FundListFilterItem';
import { ReactComponent as FilterIcon } from 'common-assets/assets/icons/filters.svg';
import clsx from 'clsx';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useIsMobile } from '../utils/useIsMobile';
import { SearchField } from '../form/SearchField/SearchField';
import { Checkbox } from '../Checkbox/Checkbox';
import { Dropdown } from '../form/Dropdown/Dropdown';
import { Card } from '../Card/Card';
import { Accordion } from '../Accordion/Accordion';
import { FundListExpandedButton } from './components/FundListExpandedButton/FundListExpandedButton';
import { FundListFilterProps } from './tools/FundList.model';
import { Typography } from '../Typography/Typography';
import { FormControlLabel } from '../form/FormControlLabel/FormControlLabel';
import { IconButton } from '../IconButton/IconButton';
import { getGenerateAutoTestId, AutoTestIdPrefix } from '../utils/testAutomation.utils';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';
import { useUniqueWcagValues } from 'common-react';

import useStyles from './FundList.style';

const FundListFilter = ({
  actions,
  resultsNumber,
  hasUnitCategorySelect = false,
  hasAdequacyWithTargetMarketSelect = false,
  showWalletCheckbox,
  filterConfig,
  renderFundTypeDropdown,
  renderAutocomplete,
  renderRiskDropdown,
  renderUmbrellaDropdown,
  renderHorizonDropdown,
  children,
  isTestAutomationEnabled = false,
  className,
}: FundListFilterProps) => {

  const [t] = useTranslation('components');
  const [tt] = useTranslation();
  const isMobile = useIsMobile();
  const theme = useTheme();
  const isBelowSl = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sl));
  const showUmbrellaInSummary = !hasUnitCategorySelect && !isBelowSl;
  const showUmbrellaInMore = hasUnitCategorySelect || isBelowSl;
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
  const c = useStyles({ hasUnitCategorySelect, isAccordionExpanded });
  const generateAutoTestId = getGenerateAutoTestId(isTestAutomationEnabled);
  const { getUniqueValue } = useUniqueWcagValues();
  const esgDescriptionId = getUniqueValue('esg-filter-description');
  const walletDescriptionId = getUniqueValue('wallet-filter-description');
  const adequacyDescriptionId = getUniqueValue('adequacy-filter-description');
  const checkboxAutoFilterDescription = t('fund-list.checkbox-auto-filter-description');
  const renderUnitCategoryDropdown = () => (
    <div className={c.unitCategoryDropdownWrapper}>
      <Dropdown
        {...filterConfig.unitCategory}
        className={c.unitCategoryDropdown}
        autoTestId={generateAutoTestId?.(AutoTestIdPrefix.DROPDOWN, filterConfig.unitCategory.label)}
        autoFilterDescription={t('fund-list.category-auto-filter-description')}
      />
    </div>
  );
  const filterAutocompletePlaceholderTextKey = 'fund-list.autocomplete-placeholder';

  const renderSearch = () => <div className={c.searchBox}>
    <SearchField
      name="fundsSearch"
      value={filterConfig.search.value}
      onChange={filterConfig.search.onChange}
      label={t(filterAutocompletePlaceholderTextKey)}
      placeholder={t(filterAutocompletePlaceholderTextKey)}
      clearButtonAriaLabel={t('fund-list.autocomplete-clear-button-aria-label')}
      autoFilterDescription={t('fund-list.search-auto-filter-description')}
      resultsCount={resultsNumber}
      resultsAriaLabel={t('fund-list.search-results-count')}
      fullWidth={true}
      onClear={() => {
        filterConfig.search.clearFilter();
        actions.removeFilter({ value: '', filterType: 'search' });
      }}
      autoTestId={generateAutoTestId?.(AutoTestIdPrefix.SEARCH_FIELD, filterAutocompletePlaceholderTextKey)}
    />
  </div>


  const renderAdequacyCheckBox = () => (
    <div className={c.checkboxWrapperItem}>
      <FormControlLabel
        className={c.checkboxControlLabel}
        control={
          <Checkbox
            id="adequacyFund"
            {...filterConfig.adequacyWithTargetMarket}
            onChange={(_, isChecked) =>
              filterConfig.adequacyWithTargetMarket.onChange(isChecked)
            }
            inputProps={{
              'aria-describedby': adequacyDescriptionId,
            }}
          />
        }
        label={t("fund-list.options.adequacy-with-target-market.label")}
      />
      <VisuallyHidden id={adequacyDescriptionId}>
        {checkboxAutoFilterDescription}
      </VisuallyHidden>
    </div>
  );

  const renderWalletCheckbox = () => (
    <div className={c.checkboxWrapperItem}>
      <FormControlLabel
        className={c.checkboxControlLabel}
        control={
          <Checkbox
            {...filterConfig.wallet}
            onChange={(_, isChecked) => filterConfig.wallet.onChange(isChecked)}
            id="wallet"
            inputProps={{
              'aria-describedby': walletDescriptionId,
            }}
          />
        }
        label={t("fund-list.options.wallet.label")}
      />
      <VisuallyHidden id={walletDescriptionId}>
        {checkboxAutoFilterDescription}
      </VisuallyHidden>
    </div>
  );

  const renderEsgCheckbox = () => (
    <div className={c.checkboxWrapperItem}>
      <FormControlLabel
        className={c.checkboxControlLabel}
        control={
          <Checkbox
            {...filterConfig.esg}
            onChange={(_, isChecked) => filterConfig.esg.onChange(isChecked)}
            id="esg"
            inputProps={{
              'aria-describedby': esgDescriptionId,
            }}
          />
        }
        label={t("fund-list.options.esg.label")}
      />
      <VisuallyHidden id={esgDescriptionId}>
        {checkboxAutoFilterDescription}
      </VisuallyHidden>
    </div>
  );

  const renderMobileMainDropdown = () => hasUnitCategorySelect ? renderUnitCategoryDropdown() : renderAutocomplete(filterConfig, { hasUnitCategorySelect, t, mobileButtonClassName: c.autocompleteMobileButton, generateAutoTestId });

  return (
    <>
      {isOpenModal && (
        <FundListFilterModal
          filterConfig={filterConfig}
          resultsNumber={resultsNumber}
          onCloseModal={() => setIsOpenModal(false)}
          hasAdequacyWithTargetMarketSelect={hasAdequacyWithTargetMarketSelect}
          showWalletCheckbox={showWalletCheckbox}
        />
      )}
      <Card
        size="big"
        fullWidth={false}
        className={clsx(className, c.filterPanel, c.fundListFilters)}
      >
        {children}
        <div className={c.filterPanelMobile}>
          {isMobile && (
            <>
              {renderMobileMainDropdown()}
              <div
                className={clsx({
                  [c.filterPanelMobileContent]: true,
                  [c.filterPanelMobileContentWithUnitCategorySelect]:
                    hasUnitCategorySelect,
                })}
              >
                {hasUnitCategorySelect &&
                  renderAutocomplete(filterConfig, {
                    hasUnitCategorySelect,
                    t,
                    mobileButtonClassName: c.autocompleteMobileButton,
                    generateAutoTestId,
                  })}
                <IconButton
                  variant="secondary"
                  icon={<FilterIcon />}
                  onClick={() => setIsOpenModal(true)}
                  ariaLabel={t("fund-list.filter-button-aria-label")}
                />
              </div>
            </>
          )}
        </div>
        {!isMobile && (
          <div className={c.container}>
            <div className={c.dropdownsWrapper}>
              <Accordion
                focusVisible={false}
                expanded={isAccordionExpanded}
                titleClassName={c.accordionTitleWrapper}
                detailsClassName={c.accordionDetails}
                shouldRemoveWcagAttributesFromAccordionSummary={true}
                expandElement={
                  <FundListExpandedButton
                    isAccordionExpanded={isAccordionExpanded}
                    onExpand={() => setIsAccordionExpanded((s) => !s)}
                    autoTestId={isAccordionExpanded ? generateAutoTestId?.(AutoTestIdPrefix.BUTTON, 'fund-list.less-filters') : generateAutoTestId?.(AutoTestIdPrefix.BUTTON, 'fund-list.more-filters')}
                  />
                }
                title={
                  <>
                    <FundListFilterItem>{renderSearch()}</FundListFilterItem>
                    {hasUnitCategorySelect && (
                      <FundListFilterItem>
                        {renderUnitCategoryDropdown()}
                      </FundListFilterItem>
                    )}
                    <FundListFilterItem>
                      {renderFundTypeDropdown(filterConfig, {
                        className: c.dropdown,
                        t,
                        generateAutoTestId,
                      })}
                    </FundListFilterItem>
                    <FundListFilterItem>
                      {renderRiskDropdown(filterConfig, {
                        className: c.dropdown,
                        t,
                        generateAutoTestId,
                      })}
                    </FundListFilterItem>
                    {showUmbrellaInSummary && (
                      <FundListFilterItem>
                        {renderUmbrellaDropdown(filterConfig, {
                          className: c.dropdown,
                          t,
                          generateAutoTestId,
                        })}
                      </FundListFilterItem>
                    )}
                  </>
                }
              >
                <div className={c.dropdownsAccordion}>
                  {showUmbrellaInMore && (
                    <FundListFilterItem
                      className={c.dropdownsAccordionSmallItem}
                    >
                      {renderUmbrellaDropdown(filterConfig, {
                        className: c.dropdown,
                        t,
                        generateAutoTestId,
                      })}
                    </FundListFilterItem>
                  )}
                  <FundListFilterItem className={c.dropdownsAccordionSmallItem}>
                    {renderHorizonDropdown(filterConfig, {
                      className: c.dropdown,
                      t,
                      generateAutoTestId,
                    })}
                  </FundListFilterItem>
                  <FundListFilterItem className={c.checkboxesFilterItem}>
                    <Typography className={c.showLabel} fontWeight="bold">
                      {tt("show")}
                    </Typography>
                    <div className={c.checkboxesWrapper}>
                      {hasAdequacyWithTargetMarketSelect &&
                        renderAdequacyCheckBox()}
                      {showWalletCheckbox && renderWalletCheckbox()}
                      {renderEsgCheckbox()}
                    </div>
                  </FundListFilterItem>
                </div>
              </Accordion>
            </div>
          </div>
        )}
        {filterConfig.isAnyFilter && (
          <FundListFilterChips filterConfig={filterConfig} actions={actions} isTestAutomationEnabled={isTestAutomationEnabled} />
        )}
      </Card>
    </>
  );
};

export default FundListFilter;
